import { makeStyles } from "tss-react/mui";
import { MiViewHouse, MiViewIS } from "@miplatform/assets";
import { PRINT_ORIENTATION } from "@miplatform/constants";
import { getRootUrl } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

/**
 * MiPrintTemplate component for rendering a printable document with a header, content, and footer.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} [props.account={}] - Account details including company info, contact details, and licenses.
 * @param {String} [props.orientation=PRINT_ORIENTATION.PORTRAIT] - Orientation of the template (portrait or landscape).
 * @param {Array<{ label: string, value: string }>} [props.headerFields=[]] - Fields to be displayed in the header.
 * @param {Object} [props.highlightedHeaderField=null] - Optional highlighted field in the header.
 * @param {Boolean} [props.hideWatermark=false] - Flag to hide the watermark.
 * @param {Boolean} [props.hideFooter=false] - Flag to hide the footer.
 * @param {React.ReactNode} props.children - Content to be rendered inside the template.
 * @returns {JSX.Element} The rendered MiPrintTemplate component.
 */
export const MiPrintTemplate = ({
  account = {},
  orientation = PRINT_ORIENTATION.PORTRAIT,
  headerFields = [],
  highlightedHeaderField = null,
  statusHeaderField = null,
  hideWatermark,
  hideFooter,
  hideHeader,
  children,
}) => {
  const API_URL = getRootUrl();
  const {
    legalName,
    phoneNumber,
    facsimileNumber,
    website,
    email,
    address,
    watermark,
    logo,
    licenses,
    invoiceTermsAndConditionsHtml,
  } = account;
  const { classes, theme } = useStyles({ orientation });

  const renderHeaderFields = () =>
    headerFields.map(({ label, value }) => (
      <Box key={label} className={classes.column}>
        <Typography className={classes.headerSubtitle} fontSize="inherit">
          {label}
        </Typography>
        <Typography fontSize="inherit" color={theme.palette.text.primary}>
          {value}
        </Typography>
      </Box>
    ));

  return (
    <Box className={classes.root}>
      {watermark && !hideWatermark && (
        <img
          className={classes.watermark}
          src={`${API_URL}/${watermark?.referenceId}`}
          alt="watermark"
        />
      )}
      {!hideHeader && (
        <Box className={classes.header}>
          <Box className={classes.headerSection}>
            {logo && (
              <img
                src={`${API_URL}/${logo?.referenceId}`}
                alt="Company Logo"
                className={classes.headerLogo}
              />
            )}
            <Typography
              fontSize={theme.typography.pxToRem(13)}
              fontWeight="inherit"
            >
              {address?.addressLine1 && (
                <>
                  {address?.addressLine1}
                  <br />
                </>
              )}
              {`${address?.city || ""}${address?.city ? ", " : ""}${
                address?.regionCode || ""
              } ${address?.postalCode || ""}`}
            </Typography>
          </Box>
          <Box className={classes.headerSection} flex={1}>
            <Box className={classes.headerNameSection}>
              <Box className={classes.headerUnderline}>
                <Typography className={classes.headerTitle}>
                  {legalName || ""}
                </Typography>
                {highlightedHeaderField && (
                  <Box className={classes.headerHighlightedField}>
                    <Typography className={classes.hightlightedFieldLabel}>
                      {highlightedHeaderField.label}
                    </Typography>
                    <Typography className={classes.hightlightedFieldValue}>
                      {highlightedHeaderField.value}
                    </Typography>
                  </Box>
                )}
                {statusHeaderField && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography
                      fontSize="inherit"
                      color={theme.palette.common.black}
                      fontWeight={theme.fontWeight.semiBold}
                    >
                      {statusHeaderField.label}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginLeft: theme.spacing(1),
                        padding: theme.spacing(0.5, 1),
                        borderRadius: theme.spacing(0.5),
                        backgroundColor: `${statusHeaderField.value?.color}2A`,
                        border: `1px solid ${statusHeaderField.value?.color}`,
                        color: statusHeaderField.value?.color,
                      }}
                    >
                      <Typography fontSize="inherit">
                        {statusHeaderField.value?.status}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            <Box className={classes.headerRow}>
              <Box className={classes.headerInformationSection}>
                <Typography fontSize="inherit" fontWeight="inherit">
                  {website && (
                    <>
                      {website}
                      <br />
                    </>
                  )}
                  {email || ""}
                </Typography>
                <Typography fontSize="inherit" fontWeight="inherit">
                  {phoneNumber && (
                    <>
                      {`Phone: ${phoneNumber}`}
                      <br />
                    </>
                  )}
                  {facsimileNumber ? `Fax: ${facsimileNumber}` : null}
                </Typography>
              </Box>
              {headerFields.length ? (
                <Box className={classes.headerFieldsSection}>
                  {renderHeaderFields()}
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      )}
      <Box className={classes.content}>{children}</Box>
      {!hideFooter && (
        <Box className={classes.footer}>
          {invoiceTermsAndConditionsHtml && (
            <Box
              className={classes.footerSection}
              dangerouslySetInnerHTML={{
                __html: invoiceTermsAndConditionsHtml,
              }}
            />
          )}
          <Box>
            <Box className={classes.footerBar} />
            <Box className={classes.footerLogosSection}>
              <Box className={classes.column}>
                <img src={MiViewHouse} alt="footer-logo" />
                <Typography className={classes.footerLogoText}>
                  MiBuild
                </Typography>
              </Box>
              {licenses.map((license) => (
                <Box key={license.id} className={classes.licenseSection}>
                  <Typography fontSize="inherit">{`${license.name} License`}</Typography>
                  <Typography
                    className={classes.licenseText}
                    fontSize="inherit"
                  >
                    {license.number}
                  </Typography>
                </Box>
              ))}
              <Box className={classes.column}>
                <Typography className={classes.footerPoweredByText}>
                  Powered By
                </Typography>
                <img src={MiViewIS} alt="footer-logo" />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme, { orientation }) => ({
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: orientation === PRINT_ORIENTATION.PORTRAIT ? "8.5in" : "100%",
    padding: theme.spacing(6),
    paddingBottom: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    minHeight: "100vh",
    gap: theme.spacing(4),
    overflow: "hidden",
  },
  header: {
    display: "flex",
    paddingLeft: theme.spacing(1.5),
    gap: theme.spacing(4),
  },
  headerSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.fontWeight.regular,
    color: theme.palette.grey.medium,
  },
  headerTitle: {
    height: 19,
    color: theme.palette.common.black,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    lineHeight: theme.typography.pxToRem(13),
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.fontWeight.semiBold,
  },
  headerUnderline: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    borderBottom: `1px solid ${theme.palette.vibrant.light_blue_a50}`,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  headerNameSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerInformationSection: {
    display: "flex",
    gap: theme.spacing(3),
  },
  headerFieldsSection: {
    display: "flex",
    gap: theme.spacing(3),
    paddingRight: theme.spacing(2),
  },
  headerSubtitle: {
    color: theme.palette.common.black,
    fontWeight: theme.fontWeight.semiBold,
  },
  headerLogo: {
    height: 32,
  },
  headerHighlightedField: {
    border: `1px solid ${theme.palette.blue.dark}`,
    borderRadius: theme.typography.pxToRem(4),
    backgroundColor: theme.palette.blue.light,
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 0.7, 1),
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  hightlightedFieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.blue.main,
  },
  hightlightedFieldValue: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.fontWeight.bold,
    color: theme.palette.blue.main,
  },
  footer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  footerSection: {
    width: "100%",
    fontSize: theme.typography.pxToRem(11),
    fontWeight: theme.fontWeight.regular,
    backgroundColor: theme.palette.grey.light,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.grey.accent}`,
    gap: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
  },
  footerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  column: {
    display: "flex",
    flexDirection: "column",
  },
  footerSubtitle: {
    fontWeight: theme.fontWeight.semiBold,
  },
  footerBar: {
    height: 5,
    backgroundColor: "#2C98F0", // COLORS WILL BE CONFIGURED FROM THE BACKEND IN THE FUTURE
    borderTopLeftRadius: theme.spacing(0.75),
    borderTopRightRadius: theme.spacing(0.75),
  },
  footerLogosSection: {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    backgroundColor: "#38474F", // COLORS WILL BE CONFIGURED FROM THE BACKEND IN THE FUTURE
  },
  footerLogoText: {
    fontWeight: theme.fontWeight.bold,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
  },
  footerPoweredByText: {
    textAlign: "center",
    fontWeight: theme.fontWeight.regular,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(10),
  },
  licenseSection: {
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(10),
    padding: theme.spacing(1, 2, 1, 2),
    backgroundColor: theme.palette.grey.accent,
  },
  licenseText: {
    color: theme.palette.grey.main,
  },
  content: {
    marginTop: theme.spacing(2),
    width: "100%",
    flex: 1,
  },
  watermark: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    width: "70%",
    height: "70%",
    padding: theme.spacing(6),
    objectFit: "contain",
  },
}));
